// ---------------------------------------------------------------------------
// Alert card component for the monitoring dashboard
// ---------------------------------------------------------------------------

import type { admin } from '@noxverify/api-contracts';
import { StatusBadge } from './StatusBadge';
import { useFormatters } from '../hooks/useFormatters';

interface AlertCardProps {
  alert: admin.MonitoringAlertResponse;
  onAcknowledge?: (alertId: string) => void;
  onResolve?: (alertId: string) => void;
  onDismiss?: (alertId: string) => void;
}

const SEVERITY_BORDER: Record<string, string> = {
  critical: 'border-s-danger',
  high: 'border-s-warning',
  medium: 'border-s-warning',
  low: 'border-s-hairline-strong',
};

export function AlertCard({ alert, onAcknowledge, onResolve, onDismiss }: AlertCardProps) {
  const f = useFormatters();
  const borderClass = SEVERITY_BORDER[alert.severity] ?? 'border-s-hairline-strong';
  const isActionable = alert.status === 'new' || alert.status === 'acknowledged';

  // Parse details JSON safely
  let detailText = alert.details;
  try {
    const parsed = JSON.parse(alert.details);
    if (typeof parsed === 'object' && parsed !== null && 'message' in parsed) {
      detailText = parsed.message as string;
    }
  } catch {
    // Use raw string
  }

  return (
    <div className={`bg-surface-raised rounded-sm border border-hairline border-s-4 ${borderClass} p-4`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <StatusBadge value={alert.severity} variant="priority" />
            <StatusBadge value={alert.status} />
            <span className="text-xs text-ink-muted font-mono">{alert.alertType}</span>
          </div>
          <p className="text-sm text-ink mt-1">{detailText}</p>
          <div className="flex items-center gap-3 mt-2 text-xs text-ink-muted">
            <span>Entity: <span className="font-mono">{alert.entityId}</span></span>
            <span>{f.date(alert.createdAt, { dateStyle: 'medium', timeStyle: 'short' })}</span>
          </div>
          {alert.resolution && (
            <p className="text-xs text-ink-muted mt-1">
              Resolution: {alert.resolution}
              {alert.resolvedAt && (
                <span className="ms-2">({f.date(alert.resolvedAt, { dateStyle: 'medium', timeStyle: 'short' })})</span>
              )}
            </p>
          )}
        </div>

        {isActionable && (
          <div className="flex flex-col gap-1 flex-shrink-0">
            {alert.status === 'new' && onAcknowledge && (
              <button
                type="button"
                onClick={() => onAcknowledge(alert.id)}
                className="px-2.5 py-1 text-xs font-medium bg-primary-tint hover:bg-primary-tint text-primary rounded transition-colors"
              >
                Acknowledge
              </button>
            )}
            {onResolve && (
              <button
                type="button"
                onClick={() => onResolve(alert.id)}
                className="px-2.5 py-1 text-xs font-medium bg-primary-tint hover:bg-primary-tint text-primary rounded transition-colors"
              >
                Resolve
              </button>
            )}
            {onDismiss && (
              <button
                type="button"
                onClick={() => onDismiss(alert.id)}
                className="px-2.5 py-1 text-xs font-medium bg-surface-sunken hover:bg-surface-sunken text-ink rounded transition-colors"
              >
                Dismiss
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
